import NativeProductCameraAssistant from './NativeProductCameraAssistant';
import type { ProductScan } from './ProductCameraAssistant';
import { CSSProperties, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import io from 'socket.io-client';
import { SERVER_ADDRESS, SOCKET_SERVER_URL } from '../../Component/server';
import { BRIDGE_CHANNEL, nativeRequest } from './nativeBridge';
import BroadcastChat from './BroadcastChat';
import { usePurchaseNotices } from '../User/Live/PurchaseNotice';
import BroadcastNotification, { BroadcastDialog } from './BroadcastNotification';
import LiveHostStatus from '../../Component/UI/LiveHostStatus';
import ProductThumbnail from '../../Component/UI/ProductThumbnail';
import RemainingStock from '../Manage/LiveManage/RemainingStock';
import useOptionInventory from '../User/Live/useOptionInventory';
import { InventoryUpdate } from '../User/Live/inventory';
import { getProductSaleStatus, hasProductExposure, showExposurePrice } from '../../Component/tool/productExposure';
import { SellerNotice, sellerNoticeUpdated, useSellerChat } from './sellerNotice';
import SellerChatFeed from '../../Component/UI/SellerChatFeed';
import ScrollingLiveTitle from '../../Component/UI/ScrollingLiveTitle';
import BroadcastProductsPanel from './BroadcastProductsPanel';
import { useBroadcastWorkspace } from './useBroadcastWorkspace';
import { maximumFontSize, minimumFontSize, useChatFontSize } from './useTabletFontSize';
import './BroadcastStudio.css';
import PriceVisibilityButton from './PriceVisibilityButton';

const TABLET_HEADER_HEIGHT = 64;

type Device = { id: string; name: string; position?: 'front' | 'back' | 'unknown'; supportsTapFocus?: boolean };
export type BroadcastState = {
  qrActive?: boolean; qrScan?: ProductScan;
  previewFrame?: { left: number; top: number; width: number; height: number };
  layout?: 'phone' | 'tablet';
  viewportHeight?: number;
  fullViewportHeight?: number;
  orientation?: 'portrait' | 'landscape';
  seq: number; liveInfo: { integration_test?: boolean; live_title: string; live_start: string; live_finish: string; live_status: string; channel_name: string } | null;
  liveStatus: 'start' | 'end'; isRehearsal: boolean; sessionChecking: boolean; sessionCheckFailed: boolean;
  starting: boolean; pendingEndStatus: string | null; connectionState: string; startedAt: number | null;
  videoDevices: Device[]; audioDevices: Device[]; selectedVideo: string; selectedAudio: string;
  permissionsReady: boolean; cameraOn: boolean; micMuted: boolean; switchingCamera: boolean;
  previewMirrored?: boolean;
  networkWarning: boolean; thermalStatus: string; micToast: string | null; currentAudioDeviceName: string;
  startEvent: number; endedEvent: number;
};
export const scheduledTime = (value?: string) => {
  if (!value) return NaN;
  const normalized = value.replace(' ', 'T');
  return Date.parse(/[zZ]$|[+-]\d\d:\d\d$/.test(normalized) ? normalized : `${normalized}+09:00`);
};
const elapsed = (seconds: number) => [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60].map(value => String(value).padStart(2, '0')).join(':');
const deviceLabel = (device: Device) => `${device.name}${device.position === 'front' ? ' (전면)' : device.position === 'back' ? ' (후면)' : ''}`;
function TabletDeviceSelect({ label, devices, value, disabled, onChange }: {
  label: string; devices: Device[]; value: string; disabled: boolean; onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();
  const selected = devices.find(device => device.id === value);
  useLayoutEffect(() => {
    if (open) (menu.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]') ?? menu.current?.querySelector<HTMLButtonElement>('[role="option"]'))?.focus();
  }, [open]);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  return <div className="studio-device-field">
    <span id={`${id}-label`}>{label}</span>
    <div ref={root} className="studio-device-select" onBlur={event => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
    }}>
      <button ref={trigger} type="button" role="combobox" aria-labelledby={`${id}-label`}
        aria-expanded={open} aria-controls={`${id}-options`} aria-haspopup="listbox" disabled={disabled}
        onClick={() => setOpen(!open)} onKeyDown={event => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setOpen(true); }
        }}>
        <span>{selected ? deviceLabel(selected) : '선택'}</span><span aria-hidden="true">⌄</span>
      </button>
      {open && <div ref={menu} id={`${id}-options`} className="studio-device-options" role="listbox" aria-labelledby={`${id}-label`}
        onKeyDown={event => {
          const options = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="option"]'));
          const index = options.indexOf(document.activeElement as HTMLButtonElement);
          if (event.key === 'Escape') { event.preventDefault(); setOpen(false); trigger.current?.focus(); }
          else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
            options[next]?.focus();
          }
        }}>
        {devices.map(device => <button type="button" key={device.id} role="option" aria-selected={device.id === value} tabIndex={-1}
          onClick={() => { setOpen(false); trigger.current?.focus(); onChange(device.id); }}>{deviceLabel(device)}</button>)}
      </div>}
    </div>
  </div>;
}
const loadSellerNotice = () => nativeRequest<SellerNotice[]>('seller-notice');
function productImage(path: string) {
  if (/^https?:\/\//.test(path)) return path;
  return path.startsWith('/') ? `${new URL(SERVER_ADDRESS).origin}${path}` : `${SERVER_ADDRESS}openfile/${path}`;
}

// The existing app keeps credentials and publishing checks native. The web only waits for readiness.
async function waitForBroadcastRelease(seq: number, mode: '시작' | '리허설', signal: AbortSignal, onWaiting: () => void) {
  const deadline = Date.now() + 65000;
  while (!signal.aborted) {
    let data: { isLive: boolean; live_start: string; live_finish: string; integration_test?: boolean };
    try {
      const response = await axios.get(`${SERVER_ADDRESS}aws-ivs/broadcast-ready`, { params: { live_seq: seq }, timeout: 5000, signal });
      if (typeof response.data?.isLive !== 'boolean') throw new Error('Invalid readiness response');
      data = response.data;
    } catch {
      if (signal.aborted) return false;
      throw new Error('송출 종료 상태를 확인하지 못했습니다. 잠시 후 다시 시도해주세요.');
    }
    if (signal.aborted) return false;
    const cutoff = scheduledTime(data.live_start) - 300000;
    const allowed = data.integration_test || (mode === '리허설' ? Date.now() < cutoff : Date.now() >= cutoff && Date.now() < scheduledTime(data.live_finish));
    if (!allowed) throw new Error(mode === '리허설'
      ? '리허설은 예약 시작 5분 전까지만 가능합니다.'
      : '라이브 시작 가능 시간이 종료되었습니다. 예약 시간을 확인해주세요.');
    if (!data.isLive) return true;
    onWaiting();
    if (Date.now() >= deadline) throw new Error('기존 송출 종료를 확인하지 못했습니다. 다른 탭이나 기기의 송출 상태를 확인한 뒤 다시 시도해주세요.');
    await new Promise<void>(resolve => {
      const finish = () => { clearTimeout(timer); signal.removeEventListener('abort', finish); resolve(); };
      const timer = setTimeout(finish, 2000);
      signal.addEventListener('abort', finish, { once: true });
      if (signal.aborted) finish();
    });
  }
  return false;
}

export default function BroadcastStudio() {
  const seq = Number(useParams().seq);
  const [state, setState] = useState<BroadcastState | null>(null);
  const [legacyIntegrationLive, setLegacyIntegrationLive] = useState<number | null>(null);
  const needsLegacyIntegration = state?.seq === seq && !!state?.liveInfo && state.liveInfo.integration_test === undefined && state.liveInfo.live_status !== '취소';
  useEffect(() => {
    setLegacyIntegrationLive(null);
    if (!needsLegacyIntegration) return;
    const controller = new AbortController();
    // Older apps omit this public flag from their snapshot; publishing credentials stay native.
    void axios.get(`${SERVER_ADDRESS}live/get-live-info`, { params: { seq }, timeout: 5000, signal: controller.signal })
      .then(({ data }) => {
        if (!controller.signal.aborted && data?.seq === seq && (data.integration_test === true || data.integration_test === 1)
          && ['예약', '리허설', '방송중', '종료'].includes(data.live_status)
          && Number.isFinite(scheduledTime(data.live_start)) && Number.isFinite(scheduledTime(data.live_finish))
          && scheduledTime(data.live_finish) > scheduledTime(data.live_start)) setLegacyIntegrationLive(seq);
      }).catch(() => { /* Keep the existing schedule when public metadata is unavailable. */ });
    return () => controller.abort();
  }, [seq, needsLegacyIntegration]);
  const [viewport, setViewport] = useState(() => ({ height: window.visualViewport?.height ?? window.innerHeight, offsetTop: window.visualViewport?.offsetTop ?? 0 }));
  useLayoutEffect(() => {
    const resize = () => setViewport({ height: window.visualViewport?.height ?? window.innerHeight, offsetTop: Math.max(0, window.visualViewport?.offsetTop ?? 0) });
    window.visualViewport?.addEventListener('resize', resize);
    window.visualViewport?.addEventListener('scroll', resize);
    window.addEventListener('resize', resize);
    resize();
    return () => { window.visualViewport?.removeEventListener('resize', resize); window.visualViewport?.removeEventListener('scroll', resize); window.removeEventListener('resize', resize); };
  }, []);
  const tablet = state?.layout === 'tablet';
  const { fontSize, changeFontSize } = useChatFontSize(tablet ? 'tablet' : 'phone');
  const [chatExpanded, setChatExpanded] = useState(false);
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  useEffect(() => { setChatExpanded(false); }, [seq, tablet]);
  const [workspaceVersion, setWorkspaceVersion] = useState(0);
  const refreshCurrentProduct = useRef(() => {});
  const workspace = useBroadcastWorkspace(seq, tablet && !!state?.liveInfo, workspaceVersion);
  const [liveNotice, setLiveNotice] = useState<string | null>(null);
  useEffect(() => { setLiveNotice(null); }, [seq]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const focusStart = useRef<{ id: number; x: number; y: number } | null>(null);
  const [focusPoint, setFocusPoint] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (!focusPoint) return;
    const timer = window.setTimeout(() => setFocusPoint(null), 1000);
    return () => window.clearTimeout(timer);
  }, [focusPoint]);
  const [restartPreparing, setRestartPreparing] = useState<'시작' | '리허설' | null>(null);
  const startWaitAbort = useRef<AbortController | null>(null);
  useEffect(() => () => { startWaitAbort.current?.abort(); }, [seq]);
  const [now, setNow] = useState(Date.now);
  const [viewers, setViewers] = useState(0);
  const [serverDuration, setServerDuration] = useState<{ value: string; at: number } | null>(null);
  const [receivedProduct, setProduct] = useState<any>(null);
  const product = tablet && workspace.data ? workspace.data.products.find(item => hasProductExposure(item)) : receivedProduct;
  const [inventoryUpdate, setInventoryUpdate] = useState<InventoryUpdate>();
  const latestInventory = useRef<InventoryUpdate | undefined>(undefined);
  useEffect(() => { latestInventory.current = undefined; setInventoryUpdate(undefined); }, [seq]);
  const optionInventory = useOptionInventory(product?.seq, product?.option_check === 'y', inventoryUpdate, undefined, true);
  const sellerChat = useSellerChat(seq, loadSellerNotice, Boolean(state?.liveInfo));
  const { notices, showPurchaseNotice } = usePurchaseNotices(tablet ? 6 : 5);
  const [showNotification, setShowNotification] = useState(false);
  const [showEnded, setShowEnded] = useState(false);
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);
  const toastSequence = useRef(0);
  const showToast = useCallback((text: string) => setToast({ id: ++toastSequence.current, text }), []);
  const startEvent = useRef(0), endedEvent = useRef(0), actionPending = useRef(false);
  const dismissNotification = useCallback(() => setShowNotification(false), []);

  useLayoutEffect(() => {
    document.documentElement.classList.add('broadcast-overlay');
    return () => document.documentElement.classList.remove('broadcast-overlay');
  }, []);
  useEffect(() => {
    let active = true;
    const update = (next: BroadcastState) => {
      if (!active || next?.seq !== seq) return;
      setState(next);
      if (next.startEvent > startEvent.current) { startEvent.current = next.startEvent; setShowNotification(true); }
      if (next.endedEvent > endedEvent.current) { endedEvent.current = next.endedEvent; setShowEnded(true); }
    };
    const receive = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data);
        if (message.channel === BRIDGE_CHANNEL && message.version === 1 && ['state', 'resume'].includes(message.type)) update(message.state);
      } catch { /* Ignore unrelated window messages. */ }
    };
    window.addEventListener('message', receive);
    nativeRequest<BroadcastState>('ready').then(update).catch(failure => { if (active) setError(failure.message); });
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => { active = false; clearInterval(timer); window.removeEventListener('message', receive); };
  }, [seq]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => { if (state?.micToast) showToast(state.micToast); }, [state?.micToast, showToast]);

  useEffect(() => {
    if (!state?.liveInfo || tablet) return;
    let active = true, running = false;
    const refresh = async () => {
      if (running) return;
      running = true;
      try {
        const rows = await nativeRequest<{ content: string }[]>('live-notice');
        if (active && Array.isArray(rows)) setLiveNotice(rows.map(row => row.content).filter(Boolean).join('\n'));
      } catch { /* Keep the last confirmed urgent notice while reconnecting. */ }
      finally { running = false; }
    };
    void refresh();
    const timer = window.setInterval(refresh, 2500);
    return () => { active = false; window.clearInterval(timer); };
  }, [seq, tablet, !!state?.liveInfo]);
  const channel = state?.liveInfo?.channel_name;
  useEffect(() => {
    if (!channel) return;
    let active = true, request = 0;
    const refreshProduct = () => {
      const revision = ++request;
      const inventoryAtStart = latestInventory.current;
      nativeRequest<any>('product').then(data => {
        if (!active || revision !== request) return;
        const newer = latestInventory.current;
        setProduct(newer && newer !== inventoryAtStart && newer.productSeq === data?.seq ? { ...data, current_cnt: newer.currentCnt } : data);
      }).catch(() => {});
    };
    refreshCurrentProduct.current = refreshProduct;
    const socket = io(SOCKET_SERVER_URL, { path: '/socket.io', transports: ['websocket'] });
    socket.on('connect', () => { socket.emit('join-channel', { channelId: channel }); refreshProduct(); });
    socket.on('Viewer Count', (value: unknown) => { const count = Number(value); if (Number.isFinite(count)) setViewers(Math.max(0, count)); });
    socket.on('Duration Time', (value: unknown) => { if (typeof value === 'string') setServerDuration({ value, at: Date.now() }); });
    socket.on('Product List Changed', (value: { liveSeq: number }) => {
      if (Number(value?.liveSeq) !== seq) return;
      setWorkspaceVersion(version => version + 1);
      refreshProduct();
    });
    socket.on('Inventory Changed', (inventory: InventoryUpdate) => {
      latestInventory.current = inventory; setInventoryUpdate(inventory);
      setProduct((current: any) => current?.seq === inventory.productSeq ? { ...current, current_cnt: inventory.currentCnt } : current);
      setWorkspaceVersion(version => version + 1);
    });
    socket.on('Live Notice', (rows: { content: string }[]) => { if (Array.isArray(rows)) setLiveNotice(rows.map(row => row.content).filter(Boolean).join('\n')); });
    socket.on('Seller Notice Updated', (update: { liveSeq: number }) => sellerNoticeUpdated(update.liveSeq));
    const stock = (value: any) => {
      request += 1;
      setWorkspaceVersion(version => version + 1);
      const image = value?.thumbnail_img || value?.image || value?.p_image;
      setProduct((current: any) => hasProductExposure(value) ? { ...value, image: image || (current?.seq === value.seq
        ? current.thumbnail_img || current.image || current.p_image : undefined) } : null);
      // Keep photo-less stock events light; refresh only when no photo was supplied.
      if (hasProductExposure(value) && !image) refreshProduct();
    };
    socket.on('Stock', stock); socket.on('Stock Over', stock);
    refreshProduct(); const timer = setInterval(refreshProduct, 10000);
    return () => { active = false; refreshCurrentProduct.current = () => {}; clearInterval(timer); socket.disconnect(); };
  }, [channel, seq]);

  const action = async (type: Parameters<typeof nativeRequest>[0], payload: Record<string, unknown> = {}) => {
    if (actionPending.current) return;
    actionPending.current = true; setBusy(true); setError('');
    try {
      if (type === 'start') {
        const mode = payload.mode;
        if (mode !== '시작' && mode !== '리허설') throw new Error('방송 모드를 확인해주세요.');
        if (state?.liveInfo?.live_status === '종료') setRestartPreparing(mode);
        const controller = new AbortController();
        startWaitAbort.current = controller;
        if (!(await waitForBroadcastRelease(seq, mode, controller.signal, () => setRestartPreparing(mode)))) return;
      }
      await nativeRequest(type, payload);
      if (['select-device', 'toggle-mic', 'toggle-camera', 'switch-camera', 'toggle-preview-mirror'].includes(type)) showToast('수정 완료');
    }
    catch (failure) { setError((failure as Error).message); }
    finally { startWaitAbort.current = null; actionPending.current = false; setRestartPreparing(null); setBusy(false); }
  };
  if (!state) return <main className="broadcast-studio"><div className="studio-wait" role="status">{error || '방송 화면을 연결하고 있습니다.'}</div></main>;
  const info = state.liveInfo;
  const active = state.liveStatus === 'start';
  const cutoff = scheduledTime(info?.live_start) - 300000, finish = scheduledTime(info?.live_finish);
  const integrationTest = !!info && (info.integration_test === true || (info.integration_test === undefined && state.seq === seq && legacyIntegrationLive === seq));
  const canRehearse = integrationTest ? info?.live_status !== '취소' : now < cutoff && ['예약', '리허설'].includes(info?.live_status ?? '');
  const canStart = (integrationTest || (now >= cutoff && now < finish)) && ['예약', '리허설', '방송중', '종료'].includes(info?.live_status ?? '');
  const ready = !busy && !state.starting && !state.sessionChecking && !state.sessionCheckFailed;
  const hasDevices = !!state.selectedVideo && !!state.selectedAudio;
  const duration = !active ? '00:00:00' : !state.isRehearsal && serverDuration && now - serverDuration.at < 3000 ? serverDuration.value : elapsed(Math.max(0, Math.floor((now - (state.startedAt ?? now)) / 1000)));
  const stock = product?.current_cnt == null ? null : Number(product.current_cnt);
  const image = product?.thumbnail_img || product?.image || product?.p_image;
  const status = state.sessionChecking ? '확인 중' : !active ? '준비' : state.connectionState !== 'connected' ? '연결 중' : state.isRehearsal ? 'Test' : 'Live';
  const dialogOpen = showEnded || (showNotification && active && !state.isRehearsal);
  const urgent = liveNotice ?? workspace.data?.liveNotice ?? '';
  const currentCamera = state.videoDevices.find(device => device.id === state.selectedVideo);
  const canSwitchCamera = state.videoDevices.some(device => device.id !== state.selectedVideo && device.position !== currentCamera?.position);
  const deviceControls = active && <div className="studio-device-controls" aria-label="라이브 장치 제어">
    <button type="button" className={state.micMuted ? 'device-off' : undefined} disabled={!ready} aria-label={`마이크 ${state.micMuted ? 'OFF' : 'ON'}`} title={state.micMuted ? '마이크 켜기' : '마이크 끄기'} aria-pressed={state.micMuted} onClick={() => action('toggle-mic')}>
      {tablet ? `마이크 ${state.micMuted ? 'OFF' : 'ON'}` : <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3m-4 0h8" />{state.micMuted && <path d="m3 3 18 18" />}</svg>}
    </button>
    <button type="button" className={!state.cameraOn ? 'device-off' : undefined} disabled={!ready} aria-label={`카메라 ${state.cameraOn ? 'ON' : 'OFF'}`} title={state.cameraOn ? '카메라 끄기' : '카메라 켜기'} aria-pressed={!state.cameraOn} onClick={() => action('toggle-camera')}>
      {tablet ? `카메라 ${state.cameraOn ? 'ON' : 'OFF'}` : <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="14" height="12" rx="2" /><path d="m16 10 6-4v12l-6-4" />{!state.cameraOn && <path d="m3 3 18 18" />}</svg>}
    </button>
    {(tablet || canSwitchCamera) && <button type="button" disabled={!ready || state.switchingCamera || !canSwitchCamera} aria-label={state.switchingCamera ? '전환 중…' : tablet ? '카메라 전환' : '화면전환'} title={currentCamera?.name ? `현재 카메라: ${currentCamera.name}` : undefined} onClick={() => action('switch-camera')}>
      {tablet ? state.switchingCamera ? '전환 중…' : '카메라 전환' : <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 4h-4L8 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-4ZM8 12h8m-2-2 2 2-2 2m2 3H8m2-2-2 2 2 2" /></svg>}
    </button>}
  </div>;
  const changePriceVisibility = async () => {
    const productSeq = product.seq;
    const mode = showExposurePrice(product.exposure_price_mode) ? 'seller' : 'visible';
    const result = await nativeRequest<{ success: boolean }>('product-exposure', { productSeq, mode, priceOnly: true });
    if (result?.success !== true) throw new Error('Price disclosure failed');
    setProduct((current: any) => current?.seq === productSeq ? { ...current, exposure_price_mode: mode } : current);
    workspace.reload();
  };
  const purchaseLog = <div className="studio-purchases" role="log" aria-label="구매 완료 알림" aria-live="polite">{notices.map(notice => <div key={notice.id}>{notice.content}</div>)}</div>;
  const productCard = product && hasProductExposure(product) && <article className={`studio-product${product.option_check === 'y' ? ' studio-product--options' : ''}`} data-product-metric="both" aria-label="노출 상품">
          <div className={`studio-product__exposure ${getProductSaleStatus(product.product_status) === '품절' ? 'sold-out' : showExposurePrice(product.exposure_price_mode) ? '' : 'price-hidden'}`}>
            <span>{getProductSaleStatus(product.product_status) === '품절' ? '품절' : showExposurePrice(product.exposure_price_mode) ? '가격 공개' : '가격 비공개'}</span>
            <PriceVisibilityButton key={product.seq} publicPrice={showExposurePrice(product.exposure_price_mode)} onChange={changePriceVisibility} disabled={!ready} />
          </div>
          <div className="studio-product__image"><ProductThumbnail src={image ? productImage(image) : undefined} alt={product.name} /></div>
          <div className="studio-product__name"><div className="studio-product__name-row"><strong title={product.name}>{product.name}</strong></div></div>
          <RemainingStock count={stock !== null && Number.isFinite(stock) ? Math.max(0, Math.floor(stock)) : undefined} price={Number(product.price)} />
          {product.option_check === 'y' && <div className="studio-option-stock" aria-label="옵션별 남은 수량">
            {optionInventory.loading && <p role="status">옵션 수량 확인 중</p>}
            {optionInventory.error ? <p role="alert">옵션 수량을 확인하지 못했습니다. <button type="button" onClick={optionInventory.retry}>다시 시도</button></p>
              : <ul>{optionInventory.options.map(row => <li key={row.seq}><span>{[row.option1, row.option2].filter(Boolean).join(' / ')}</span><strong>{row.current_cnt}개</strong></li>)}</ul>}
          </div>}
        </article>;
  const chat = info && <BroadcastChat key={seq} seq={seq} panel={tablet} fontScale={tablet ? fontSize / 100 : 1} enabled={active || ['리허설', '방송중'].includes(info.live_status)} onPurchaseNotification={showPurchaseNotice}
        headerControls={tablet ? <div className="studio-chat-tools">{chatExpanded && <button type="button" onClick={() => setChatExpanded(false)} aria-label="송출 화면으로 돌아가기">송출 화면</button>}<div className="studio-font-controls" role="group" aria-label="폰트 크기">
          <span>폰트 크기</span><button type="button" aria-label="폰트 크기 줄이기" disabled={fontSize <= minimumFontSize} onClick={() => changeFontSize(-10)}>−</button>
          <output aria-label="현재 폰트 크기" aria-live="polite">{fontSize}%</output>
          <button type="button" aria-label="폰트 크기 키우기" disabled={fontSize >= maximumFontSize} onClick={() => changeFontSize(10)}>+</button>
        </div></div> : undefined}
        notices={<>
          {tablet && workspace.data?.regularNotice && <aside className="studio-chat-notice" role="note" aria-label="공지"><strong>공지</strong><p>{workspace.data.regularNotice}</p></aside>}
          {(!tablet || chatExpanded) && <SellerChatFeed messages={sellerChat.messages} persistent={tablet} />}
        </>}>
        {!tablet && productCard}
      </BroadcastChat>;
  const nativeHeight = Number.isFinite(state.viewportHeight) && Number(state.viewportHeight) > 0 ? Number(state.viewportHeight) : viewport.height;
  const viewportHeight = Math.min(nativeHeight, viewport.height);
  const keyboardVisible = viewportHeight < Number(state.fullViewportHeight ?? nativeHeight) - 100;
  return <main data-keyboard-visible={!tablet && keyboardVisible ? 'true' : undefined} className={`broadcast-studio ${tablet ? 'studio-tablet' : `studio-phone studio-phone-${state.orientation === 'landscape' ? 'landscape' : 'portrait'}`}${tablet && chatExpanded ? ' studio-chat-focus' : ''}`} style={{
    '--studio-font-scale': tablet ? fontSize / 100 : 1,
    '--studio-viewport-height': !tablet ? `${viewportHeight}px` : undefined,
    '--studio-viewport-offset': !tablet ? `${viewport.offsetTop}px` : undefined,
  } as CSSProperties}>
    <div style={{ position: 'absolute', ...state.previewFrame, pointerEvents: 'none', zIndex: 100 }}>
      <NativeProductCameraAssistant active={!!state.qrActive && !dialogOpen && !keyboardVisible} scan={state.qrScan} onChanged={() => {
        setWorkspaceVersion(value => value + 1); refreshCurrentProduct.current();
      }} />
    </div>
    <div className="studio-content" aria-hidden={dialogOpen || undefined} {...(dialogOpen ? { inert: true } : {})}>
      {active && !state.cameraOn && !tablet && <div className="studio-camera-cover" aria-hidden="true" />}
      <header className="studio-header">
        <div className="studio-title"><button type="button" onClick={() => action('back')} aria-label="라이브 목록으로">‹</button><h1><ScrollingLiveTitle title={info?.live_title || '라이브 준비 중'} /></h1></div>
        {!tablet && <div className="studio-status"><span className={`studio-badge ${active ? state.isRehearsal ? 'rehearsal' : 'active' : ''}`}>{status}</span><span><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M12 6v6l4 3" /></svg>{duration}</span><span><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></svg>시청 {viewers}명</span></div>}

        <div className="studio-header-controls">
        {tablet && <span className={`studio-badge ${state.isRehearsal ? 'rehearsal' : active ? 'active' : ''}`}>{status}</span>}
        {tablet && process.env.NODE_ENV === 'development' && <span className="studio-badge" title="이 Mac의 로컬 개발 화면">로컬</span>}
        {tablet && deviceControls}
        <div className="studio-actions">
          {!tablet && deviceControls}
          {tablet && active && <button type="button" aria-expanded={showDeviceSettings} aria-controls="studio-device-settings" onClick={() => setShowDeviceSettings(value => !value)}>장치 선택</button>}
          {typeof state.previewMirrored === 'boolean' && <button type="button" className="studio-mirror-button" disabled={!ready} aria-label="좌우반전" aria-pressed={state.previewMirrored} title="셀러 프리뷰에만 적용" onClick={() => action('toggle-preview-mirror')}>
            {tablet ? '좌우반전' : <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4v16l6-8ZM21 4v16l-6-8Z" /><path d="M12 3v18" strokeDasharray="2 3" /></svg>}
          </button>}
          {state.pendingEndStatus ? <button type="button" disabled={!ready} onClick={() => action('end')}>상태 변경 다시 시도</button>
            : active ? <button type="button" className="studio-end-button" disabled={!ready} aria-label={state.isRehearsal ? '리허설 종료' : '라이브 종료'} title={state.isRehearsal ? '리허설 종료' : '라이브 종료'} onClick={() => action('end')}>
                {tablet ? state.isRehearsal ? '리허설 종료' : '라이브 종료' : <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="5" width="14" height="14" rx="2" /></svg>}
              </button>
              : <><button type="button" aria-busy={restartPreparing === '리허설'} disabled={!ready || !hasDevices || !canRehearse} onClick={() => action('start', { mode: '리허설' })}>{restartPreparing === '리허설' ? '재시작 준비 중' : info?.live_status === '리허설' ? '리허설 다시 연결' : '리허설 시작'}</button><button type="button" className="primary" aria-busy={restartPreparing === '시작'} disabled={!ready || !hasDevices || !canStart} onClick={() => action('start', { mode: '시작' })}>{restartPreparing === '시작' ? '재시작 준비 중' : info?.live_status === '종료' ? '다시 방송하기' : '라이브 시작'}</button></>}
        </div>
        </div>
        {!tablet && urgent && <aside className="studio-urgent" role="alert"><strong>긴급</strong><p>{urgent}</p></aside>}
        {!active && !canRehearse && !canStart && info && <p className="studio-hint">예약 종료 시간이 지났거나 송출할 수 없는 방송입니다.</p>}
        {state.sessionCheckFailed && <p role="alert">송출 상태를 확인하지 못했습니다. 목록에서 다시 들어와 주세요.</p>}
        {error && <p className="studio-error" role="alert">{error}</p>}
        {active && state.networkWarning && <p className="studio-warning" role="status">네트워크 연결이 불안정합니다. 안정적인 Wi-Fi 환경으로 이동해 주세요.</p>}
        {active && state.thermalStatus !== 'NONE' && <p className="studio-warning" role="status">{state.thermalStatus === 'CRITICAL' ? '기기 과열로 곧 방송이 강제 종료될 수 있습니다. 쿨러를 사용하거나 방송을 잠시 쉬어주세요.' : '기기 온도가 높아 방송이 버벅일 수 있습니다. 기기 쿨링이 필요합니다.'}</p>}
      </header>
      <div className="studio-preview" onPointerDown={event => {
        if (!ready || !state.cameraOn || !currentCamera?.supportsTapFocus || keyboardVisible || dialogOpen) return;
        if (event.target !== event.currentTarget && !(event.target as HTMLElement).classList.contains('studio-portrait-frame')) return;
        focusStart.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }} onPointerCancel={() => { focusStart.current = null; }} onPointerUp={async event => {
        const start = focusStart.current;
        focusStart.current = null;
        if (!start || start.id !== event.pointerId || !ready || !state.cameraOn || dialogOpen ||
          Math.hypot(start.x - event.clientX, start.y - event.clientY) > 8) return;
        const main = event.currentTarget.closest('main')!;
        const frame = tablet ? event.currentTarget.querySelector('.studio-portrait-frame')! : main;
        const bounds = frame.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;
        const x = (event.clientX - bounds.left) / bounds.width;
        const y = (event.clientY - bounds.top) / bounds.height;
        if (x < 0 || x > 1 || y < 0 || y > 1) return;
        const preview = event.currentTarget.getBoundingClientRect();
        const point = { x: event.clientX - preview.left, y: event.clientY - preview.top };
        if (actionPending.current) return;
        actionPending.current = true; setBusy(true); setError('');
        try {
          await nativeRequest('focus', { x: state.previewMirrored ? 1 - x : x, y, aspect: bounds.width / bounds.height });
          setFocusPoint(point);
        } catch (cause) { setError((cause as Error).message); }
        finally { actionPending.current = false; setBusy(false); }
      }}>
      {focusPoint && <span className="studio-focus-point" aria-hidden="true" style={{ left: focusPoint.x, top: focusPoint.y }} />}
      <div className="studio-portrait-frame" style={tablet && state.previewFrame ? { position: 'absolute', ...state.previewFrame, top: state.previewFrame.top - TABLET_HEADER_HEIGHT } : undefined}>
      {!tablet && purchaseLog}
      {active && !state.cameraOn && tablet && <div className="studio-camera-cover" aria-hidden="true" />}
      {tablet && <div className="studio-live-alerts">{purchaseLog}{urgent && <aside className="studio-urgent" role="alert"><strong>긴급</strong><p>{urgent}</p></aside>}</div>}
      {active && <div className="studio-camera-state">
        {!tablet && !state.cameraOn ? <div className="studio-camera-notice" role="status" aria-live="polite">
          <img src={require('../../Image/Logo.png')} alt="팬버드" />
          <span>{state.micMuted ? '카메라 · 마이크 꺼짐' : '카메라 꺼짐'}</span>
        </div> : <LiveHostStatus interrupted={false} cameraOff={!state.cameraOn} micMuted={state.micMuted} />}
      </div>}

      {!active || (tablet && showDeviceSettings) ? <section id="studio-device-settings" className="studio-devices" aria-label="장치 선택">
        {tablet ? <>
          <TabletDeviceSelect label="카메라" devices={state.videoDevices} value={state.selectedVideo} disabled={!ready || (active && !state.cameraOn)} onChange={deviceId => action('select-device', { kind: 'video', deviceId })} />
          <TabletDeviceSelect label="마이크" devices={state.audioDevices} value={state.selectedAudio} disabled={!ready} onChange={deviceId => action('select-device', { kind: 'audio', deviceId })} />
        </> : <>
          <label>카메라<select value={state.selectedVideo} disabled={!ready || (active && !state.cameraOn)} onChange={event => action('select-device', { kind: 'video', deviceId: event.target.value })}>{state.videoDevices.map(device => <option key={device.id} value={device.id}>{deviceLabel(device)}</option>)}</select></label>
          <label>마이크<select value={state.selectedAudio} disabled={!ready} onChange={event => action('select-device', { kind: 'audio', deviceId: event.target.value })}>{state.audioDevices.map(device => <option key={device.id} value={device.id}>{deviceLabel(device)}</option>)}</select></label>
        </>}
        {!state.permissionsReady && <button type="button" disabled={!ready} onClick={() => action('permissions')}>카메라·마이크 권한 허용</button>}
        {tablet && active && <button type="button" onClick={() => setShowDeviceSettings(false)}>장치 선택 닫기</button>}
      </section> : null}
      {tablet && !chatExpanded && <div className="studio-tablet-seller-chat"><SellerChatFeed messages={sellerChat.messages} persistent /></div>}
      </div>
      </div>
      <div className="studio-bottom">
      <div className="studio-tablet-workspace">
        {tablet ? <>
          <div className="studio-tablet-column studio-tablet-column--chat">
            <div className="studio-tablet-exposure">{productCard || <p>판매 노출 중인 상품이 없습니다.</p>}</div>
            {chat}
          </div>
          <div className="studio-tablet-column studio-tablet-column--products">
            <div className="studio-tablet-stats" aria-label="라이브 진행 현황">
              <div><span>진행 시간</span><b>{duration}</b></div>
              <div><span>시청자 수</span><b>{viewers}명</b></div>
              <div><span>현재 총 매출</span><strong>{workspace.data?.totalSales == null ? '—' : `${workspace.data.totalSales.toLocaleString()}원`}</strong></div>
            </div>
            <BroadcastProductsPanel data={workspace.data} error={workspace.error} saving={workspace.saving || !ready} onChange={workspace.changeExposure} onReload={workspace.reload} />
          </div>
        </> : chat}
      </div>
      </div>
      {restartPreparing ? <p className="studio-toast studio-toast--pending" role="status">기존 송출 종료를 확인하고 있습니다. 준비가 완료되면 자동으로 시작합니다.</p> : toast && <p key={toast.id} className="studio-toast" role="status">{toast.text}</p>}
    </div>
    {showNotification && active && !state.isRehearsal && <BroadcastNotification onDismiss={dismissNotification} onSent={() => { setShowNotification(false); showToast('시작 알림톡 발송을 접수했습니다.'); }} />}
    {showEnded && <BroadcastDialog title="방송이 종료되었습니다."><div className="studio-modal__actions"><button type="button" className="primary" onClick={() => action('back')}>확인</button></div></BroadcastDialog>}
  </main>;
}
